<template>
  <div
    class="drop-down"
  >
    <div
      class="drop-down__select"
      :class="{ active: openDropdown }"
    >
      <div class="drop-down__items">
        <div
          v-for="{ title, id, selected } in selectedControl"
          :key="title"
          name="chip"
        >
          <DsChip
            v-if="selected"
            :id="id"
            :title="title"
            type="removable"
            icon
            name="delete-circle"
            @click="toggleOption(id)"
          />
        </div>
        <input
          ref="refSearchValue"
          v-model="searchValue"
          type="text"
          class="drop-down__search"
          :placeholder="placeholderInputSearch"
          @focus="setInputFocus"
          @keyup="searchOption(searchValue)"
        >
      </div>
      <div
        class="drop-down__close-search"
        @click="clearSearch"
      >
        <DsIcon
          v-if="searchValue || countSelected > 0"
          name="close"
          size="large"
          space="right"
        />
      </div>
      <div
        class="drop-down__icon"
        @click="toggleDropdown('inside')"
      >
        <DsIcon :name="`menu${openDropdown ? '-up' : '-down'}`" />
      </div>
    </div>
    <div
      v-if="openDropdown"
      class="drop-down__options"
      @mouseleave="leaveDropdownOptions"
    >
      <div
        v-if="isEmpty()"
        class="drop-down__empty"
      >
        <h4>{{ notFound }}</h4>
        <p>{{ textNotFound }} "{{ searchValue }}".</p>
      </div>
      <div
        v-for="item in optionsList"
        :key="item.title"
      >
        <DsCheckbox
          :id="`${item.id}`"
          v-model="item.selected"
          :label="item.title"
          :disabled="item.disabled"
          @click.native.prevent="toggleOption(item.id)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { DsIcon } from '@ds/icons';
import { DsChip, DsCheckbox } from '@ds/components';

export default {
  name: 'DropDown',
  components: {
    DsChip,
    DsCheckbox,
    DsIcon,
  },
  props: {
    listItems: {
      type: Array,
      required: true,
    },
    limit: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      limited: false,
      searchValue: '',
      optionsList: [],
      countSelected: 0,
      selectedControl: [],
      openDropdown: false,
      arrayLabels: ['selectedControl', 'optionsList'],
      placeholderInputSearch: 'Digite ou selecione os grupos',
      notFound: 'Nenhum resultado encontrado',
      textNotFound: 'Não foi possível encontrar um grupo com o termo ',
    };
  },
  watch: {
    countSelected() {
      if (this.limit <= 0) return;

      if (this.countSelected >= this.limit && !this.limited) {
        this.limitSelectionOfOptions(true);
        this.placeholderInputSearch = '';
        this.limited = true;
        return;
      }

      if (this.limited) {
        this.limitSelectionOfOptions(false);
        this.placeholderInputSearch = 'Digite ou selecione os grupos';
        this.limited = false;
      }
    },
    listItems() {
      this.mapItemList();
    },
  },
  created() {
    this.mapItemList();
  },
  methods: {
    leaveDropdownOptions() {
      this.clearSearch();
      this.$refs.refSearchValue.blur();
      this.openDropdown = false;
    },
    async mapItemList() {
      const mappedOptions = this.listItems.map(({ id, title }) => ({
        id,
        title,
        selected: false,
        disabled: false,
      }));

      this.selectedControl = mappedOptions;
      this.optionsList = mappedOptions;
      this.countSelected = 0;

      await this.$nextTick();
      this.$emit('options-list', {
        source: 'outside',
        list: this.selectedControl.filter((option) => option.selected),
      });
    },
    limitSelectionOfOptions(disabled) {
      this.arrayLabels.forEach((label) => {
        this[label] = this.changeValue(this[label], {
          disabled,
          verificationParameter: this.conditionalToDisableOption,
          processingFunction: this.reverseDisabledValue,
        });
      });
    },
    async toggleOption(id) {
      this.arrayLabels.forEach((label) => {
        this[label] = this.changeValue(this[label], {
          id,
          verificationParameter: this.conditionalForSelection,
          processingFunction: this.reverseSelectionValue,
        });
      });

      this.$emit('options-list', {
        source: 'outside',
        list: this.selectedControl.filter((option) => option.selected),
      });

      this.countSelected = this.selectedControl.reduce((acc, option) => {
        return option.selected ? acc + 1 : acc;
      }, 0);
    },
    reverseSelectionValue(option) {
      return {
        id: option.id,
        title: option.title,
        selected: !option.selected,
        disabled: option.disabled,
      };
    },
    reverseDisabledValue(option, disabled) {
      return {
        id: option.id,
        title: option.title,
        selected: option.selected,
        disabled,
      };
    },
    conditionalForSelection(option, id) {
      return option.id === id && !option.disabled;
    },
    conditionalToDisableOption(option) {
      return !option.selected;
    },
    changeValue(
      list,
      { verificationParameter, processingFunction, id, disabled }
    ) {
      return list.map((option) =>
        verificationParameter(option, id)
          ? processingFunction(option, disabled)
          : option
      );
    },
    searchOption(value) {
      this.optionsList = this.selectedControl.filter((option) =>
        option.title.toLowerCase().includes(value.toLowerCase())
      );
    },
    async toggleDropdown(source = 'inside') {
      this.openDropdown = !this.openDropdown;
      await this.$nextTick();
      this.$emit('options-list', {
        source,
        list: this.selectedControl.filter((option) => option.selected),
      });
    },
    async clearSearch(event) {
      if (this.searchValue !== '') {
        this.searchValue = '';
        this.searchOption(this.searchValue);
        return;
      }

      if (event) {
        this.mapItemList();
        await this.$nextTick();
        this.$emit('options-list', {
          source: 'outside',
          list: this.selectedControl.filter((option) => option.selected),
        });
      }
    },
    isEmpty() {
      return !this.optionsList.length;
    },
    setInputFocus() {
      this.openDropdown = true;
      this.$emit('input-focus');
    },
  },
};
</script>

<style lang="scss" scoped>
.drop-down {
  position: relative;

  &__select {
    padding: var(--ds-spacing-stack-nano) var(--ds-spacing-stack-xs) 0 var(--ds-spacing-stack-nano);
    display: flex;
    border-radius: var(--ds-border-radius-sm);
    outline: var(--ds-border-width-hairline) solid var(--ds-color-gray-1);
    outline-offset: calc(var(--ds-border-width-hairline) * -1);

    &.active {
      outline: var(--ds-border-width-thin) solid var(--ds-color-interactive);
      outline-offset: calc(var(--ds-border-width-thin) * -1);
    }
  }

  &:focus-visible {
    border-radius: var(--ds-border-radius-sm);
    border: var(--ds-border-width-hairline) solid var(--ds-color-interactive);
    outline: none;
  }

  &__items {
    width: 100%;
    min-height: 24px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    > div {
      margin-bottom: var(--ds-spacing-stack-nano);
    }
  }

  &__search {
    width: 200px;
    height: 22px;
    margin-left: var(--ds-spacing-stack-nano);
    margin-bottom: var(--ds-spacing-stack-nano);

    &:focus-visible {
      outline: 0;
    }
  }

  &__options {
    position: absolute;
    margin-top: var(--ds-spacing-stack-quark);
    z-index: var(--zindex-1);
    background: var(--ds-color-white);
    width: 100%;
    border: var(--ds-border-width-hairline) solid var(--ds-color-gray-3);
    box-sizing: border-box;
    box-shadow: var(--ds-light-dropshadow-1);
    border-radius: var(--ds-border-radius-sm);
    max-height: 220px;
    overflow: auto;

    .ds-checkbox {
      display: flex;
      align-items: center;
      height: 40px;
      padding-left: calc(var(--ds-spacing-stack-xs) + 2px);
      margin: var(--ds-spacing-stack-quark);

      &:hover {
        background: var(--ds-color-background);
      }
    }
  }

  &__icon,
  &__close-search {
    position: relative;
    bottom: 2px;
    width: 2.5%;
    height: 22px;
    text-align: center;
  }

  &__empty {
    width: 100%;
    padding: 75px;
    text-align: center;

    h4 {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-baseline);
      text-align: center;
      color: var(--ds-color-text-dark);
    }

    p {
      font-weight: normal;
      padding-top: var(--ds-spacing-stack-nano);
      font-size: var(--ds-font-size-sm);
      line-height: 20px;
      text-align: center;
      color: var(--ds-color-text-2);
    }
  }

  div[name="chip"] {
    display: flex;
    height: 24px;
  }
}

.ds-icon {
  background: var(--ds-color-icon-2);
  color: var(--ds-color-white-text);
  font-size: calc(var(--ds-font-size-sm) + 1px);
  text-align: center;
  cursor: pointer;
}

.ds-chip {
  margin-right: var(--ds-spacing-stack-nano);
  padding: 3px var(--ds-spacing-inset-nano);
}
</style>
